import { useEnv } from 'src/composables/useEnv';
import {
  computed,
  nextTick,
  onMounted,
  reactive,
  ref,
  shallowRef,
  type ComputedRef,
  type Reactive,
  type Ref,
  type ShallowRef,
} from 'vue';
import { createInjection } from 'src/lib/createInjection';
import { useAuth } from 'src/composables/useAuth';
import {
  ExtremeMapEssentials,
  IExtremeMapRef,
  MovieLayerMeta,
  initMovieLayerMeta,
  Tab,
} from 'src/models';
import type { CommentIndexParams } from 'src/models/apis/comment/commentRequest';
import { useReactiveMasters } from 'src/composables/useMasters';
import {
  GeoItemsMeta,
  initGeoItemsMeta,
} from 'src/models/geoItem';
import type { IGeoItemSearchRef } from 'src/models/geoItemSearch';
import { dtFormat } from 'src/lib/dateHelper';
import { CustomGeoItemLayer } from 'src/models/user';
import { useShortcutKey } from 'src/composables/useShortcutKey';
import type { ImageViewModalState, ShowImageParams, SimpleModalState } from 'src/models/top';
import { provideMovieTagEdit, type MovieTagEditStore } from 'src/composables/useMovieTagEdit';
import { useMoviePlayer } from 'src/composables/useMoviePlayer';
import { provideMovieSearch, type MovieSearchStore } from 'src/composables/useMovieSearch';
import { provideGeoItemLayers, type GeoItemLayersStore } from 'src/composables/useGeoItemLayers';
import { provideMapSelection, type MapSelectionStore } from 'src/composables/useMapSelection';
import { useCar, type CarContext } from 'src/composables/useCar';
import { useRecentComment } from 'src/composables/useRecentComment';
import { useExtParams } from 'src/composables/useExtParams';
import { isAiKakuninUser } from 'src/components/Top/utils';
import { useLayout, type LayoutContext } from 'src/composables/useLayout';

interface WaitSpinnerState {
  show: boolean;
  msg: string;
  msgDefault: string;
}

interface UiState {
  waitSpinner: WaitSpinnerState;
  simpleModal: SimpleModalState;
}

// 待機スピナーのデフォルト表示文言。実行中に変化しない値のためモジュール定数として定義する。
const WAIT_SPINNER_MSG_DEFAULT = 'データロード中...';

interface TopState {
  extremeMapEssentials: ExtremeMapEssentials | null;
  // 地図が表示・サイズ確定した後に初期表示範囲へフィットさせるための trueに切り替える信号
  shouldFitInitialExtent: boolean;
  ui: UiState;
}

// 画面共有状態（レイアウト・refs・マスタ・車両など、どのドメインにも属さない
// コーディネーター固有の状態）。shell オブジェクトの構造そのものの型。
interface TopShellContext extends
  Omit<LayoutContext, 'resizeMap'>,
  Pick<
    CarContext,
    'carState' | 'groupScopeMap' | 'updateShowCarIcons' | 'onCarListRowClick' | 'onCarSearchParamsChange'
  > {
  state: Reactive<TopState>;
  tabs: Ref<Tab[]>;
  geoItemsMeta: GeoItemsMeta;
  movieLayerMeta: ShallowRef<MovieLayerMeta>;
  masters: ReturnType<typeof useReactiveMasters>['masters'];
  refExtremeMap: Ref<IExtremeMapRef | undefined>;
  dtFormat: typeof dtFormat;
  hasRoleSuperAdmin: ComputedRef<boolean>;
  isMapReady: ComputedRef<boolean>;
}

interface TopViewContext {
  shell: TopShellContext;
  imageModal: {
    imageViewModal: Reactive<ImageViewModalState>;
    showImage: (imageParams: ShowImageParams) => void;
    closeImageModal: () => void;
  };
  moviePlayer: ReturnType<typeof useMoviePlayer>;
  mapSelection: MapSelectionStore;
  movieSearch: MovieSearchStore;
  geoItemLayers: GeoItemLayersStore;
  movieTagEdit: MovieTagEditStore;
}

// 画面共有状態（レイアウト・refs・マスタ・車両）。サブコンポーネントが inject する。
export type TopShellStore = ReturnType<typeof provideTopView>['shell'];

const { provide: provideTopShellCtx, inject: injectTopShellCtx } =
  createInjection<TopShellStore>('useTopShell');

export function useTopShellStore(): TopShellStore {
  return injectTopShellCtx();
}

// Top画面の表示レイヤー既定値。ユーザーのカスタムレイヤー設定に前置される固定リスト。
const DEFAULT_LAYER_LIST: CustomGeoItemLayer[] = [
  // 車はレイヤーデータ表示のとこのチェックボックスを非表示
  { name: 'car', dispName: '車両', hideToggleCheckbox: true, isCreatable: false, isTabPanel: false, tabLabel: '' },
  {
    name: 'comment',
    dispName: '付箋',
    hideToggleCheckbox: false,
    isCreatable: true,
    isTabPanel: false,
    tabLabel: '',
  },
];

function createImageModal() {
  const imageViewModal = reactive<ImageViewModalState>({
    show: false,
    title: '',
    imageUrls: [],
    downloadFilenameTemplate: '',
    initialIndex: 0,
  });
  const showImage = (imageParams: ShowImageParams): void => {
    imageViewModal.show = true;
    imageViewModal.title = imageParams.modalTitle;
    imageViewModal.imageUrls = imageParams.imageUrls;
    imageViewModal.downloadFilenameTemplate = imageParams.downloadFilenameTemplate;
    imageViewModal.initialIndex = imageParams.initialIndex ?? 0;
  };
  const closeImageModal = (): void => {
    imageViewModal.show = false;
    imageViewModal.title = '';
    imageViewModal.imageUrls = [];
    imageViewModal.downloadFilenameTemplate = '';
    imageViewModal.initialIndex = 0;
  };
  return { imageViewModal, showImage, closeImageModal };
}

interface WireDomainStoresDeps {
  geoItemsMeta: GeoItemsMeta;
  movieLayerMeta: ShallowRef<MovieLayerMeta>;
  tabs: Ref<Tab[]>;
  refExtremeMap: Ref<IExtremeMapRef | undefined>;
  layout: ReturnType<typeof useLayout>;
  state: Reactive<TopState>;
  masters: ReturnType<typeof useReactiveMasters>['masters'];
}

// moviePlayer・mapSelection・movieSearch・geoItemLayers・新着付箋ポーリングは、
// 相互に遅延参照（クロージャ）し合うため同一関数スコープに留める必要がある
// （宣言順より前からの forward reference。各クロージャは後続の const 確定後にのみ
// 呼ばれるため安全）。useCar・useExtParams はこの相互依存を持たないため
// wireDomainStores 側へ分離できる。
function wireCoreStores(deps: WireDomainStoresDeps) {
  const { geoItemsMeta, movieLayerMeta, tabs, refExtremeMap, layout, state, masters } = deps;

  const moviePlayer = useMoviePlayer({ getMovieSearchResults: () => movieSearch.movieSearch.results });
  const { afterMoviePlayClick } = moviePlayer;

  const { startGettingRecentComments, refreshCommentTab } = useRecentComment(geoItemsMeta, masters, tabs);

  const mapSelection = provideMapSelection({
    geoItemsMeta,
    refExtremeMap,
    resizeMap: layout.resizeMap,
    simpleModal: state.ui.simpleModal,
    refreshCommentTab: () => refreshCommentTab(),
    afterMoviePlayClick,
    doAreaMovieSearch: (queryArea) => movieSearch.doAreaMovieSearch(queryArea),
    changeGeoItemLayers: (dataName) => geoItemLayers.changeGeoItemLayers(dataName),
    // geoItemSearchRefMapは全レイヤー共通のGeoItemSearchInstance型のため、
    // 'comment'キーが持つ具体的なジェネリック型は実行時の登録内容からしか保証できずキャストが必要。
    getCommentSearchRef: () =>
      geoItemLayers.geoItemSearchRefMap['comment'] as IGeoItemSearchRef<CommentIndexParams> | undefined,
    isMovieSearchResultModeNone: () => movieSearch.isMovieSearchResultModeNone.value,
    isMovieSearchResultModeArea: () => movieSearch.isMovieSearchResultModeArea.value,
    getAreaQueryRadiusMeter: () => movieSearch.movieSearch.areaQueryRadiusMeter,
  });
  const {
    mapElem,
    setMapSelectedElemInfo,
    tryRefreshCurrentSelectedElemInfo,
    afterDataLayerItemClick,
    onTabElementClick,
  } = mapSelection;

  const getSelectedElemDataName = (): string | null => mapElem.mapSelectedElemInfo?.dataName ?? null;

  const movieTagEdit = provideMovieTagEdit({ setMapSelectedElemInfo });

  const movieSearch = provideMovieSearch({
    movieLayerMeta,
    refExtremeMap,
    tabs,
    styles: layout.styles,
    afterDataLayerItemClick,
    setMapSelectedElemInfo,
    getSelectedElemDataName,
    afterMoviePlayClick,
    onTabElementClick,
  });

  const geoItemLayers = provideGeoItemLayers({
    geoItemsMeta,
    movieLayerMeta,
    refExtremeMap,
    tabs,
    waitSpinner: state.ui.waitSpinner,
    tryRefreshCurrentSelectedElemInfo,
    setMapSelectedElemInfo,
    getSelectedElemDataName,
  });

  return { moviePlayer, mapSelection, movieTagEdit, movieSearch, geoItemLayers, startGettingRecentComments };
}

function wireDomainStores(deps: WireDomainStoresDeps) {
  const { geoItemsMeta, movieLayerMeta } = deps;
  const core = wireCoreStores(deps);
  const { moviePlayer, mapSelection, movieTagEdit, movieSearch, geoItemLayers, startGettingRecentComments } = core;

  // ─── ドメインデータ（車両）──────────────────────────────────────────────
  const {
    carState,
    groupScopeMap,
    updateShowCarIcons,
    onCarListRowClick,
    onCarSearchParamsChange,
    fetchDevices,
    initCar,
  } = useCar(geoItemsMeta, {
    afterDataLayerItemClick: mapSelection.afterDataLayerItemClick,
    tryRefreshCurrentSelectedElemInfo: mapSelection.tryRefreshCurrentSelectedElemInfo,
  });
  const { handleExtParams } = useExtParams(geoItemsMeta, movieLayerMeta, {
    getAreaQueryRadiusMeter: () => movieSearch.movieSearch.areaQueryRadiusMeter,
    doAreaMovieSearch: movieSearch.doAreaMovieSearch,
    setMapSelectedElemInfo: mapSelection.setMapSelectedElemInfo,
    getNewObjForMapSelectedElem: mapSelection.getNewObjForMapSelectedElem,
    onTabElementClick: mapSelection.onTabElementClick,
    setMovieQueryStart: movieSearch.setMovieQueryStart,
    setMovieQueryDone: movieSearch.setMovieQueryDone,
    onMovieSearchResultRowClick: movieSearch.onMovieSearchResultRowClick,
  });

  return {
    moviePlayer,
    mapSelection,
    movieTagEdit,
    movieSearch,
    geoItemLayers,
    carState,
    groupScopeMap,
    updateShowCarIcons,
    onCarListRowClick,
    onCarSearchParamsChange,
    fetchDevices,
    initCar,
    startGettingRecentComments,
    handleExtParams,
  };
}

interface InitializeTopViewDeps {
  auth: ReturnType<typeof useAuth>;
  masters: ReturnType<typeof useReactiveMasters>['masters'];
  resolveMastersPromise: ReturnType<typeof useReactiveMasters>['resolveMastersPromise'];
  isProduction: boolean;
  username: ComputedRef<string>;
  state: Reactive<TopState>;
  geoItemLayers: GeoItemLayersStore;
  fetchDevices: () => Promise<void>;
  handleExtParams: () => void;
  resizeMap: () => void;
  initCar: () => Promise<void>;
  startGettingRecentComments: () => Promise<void>;
}

async function initializeTopView(deps: InitializeTopViewDeps): Promise<void> {
  await deps.resolveMastersPromise();
  const resolvedUser = deps.auth.user;
  if (!resolvedUser) return;

  deps.state.extremeMapEssentials = {
    userSettings: resolvedUser.settings,
    kpMap: deps.masters.kp.kpMap,
    geoConnections: deps.masters.kp.geoConnections,
  };

  let layerListCustom: CustomGeoItemLayer[] = resolvedUser.settings.customGeoItemLayers || [];

  // FIXME そのうち消す
  if (deps.isProduction) {
    // 本番環境の場合、AI検知結果は特定のユーザーIDの場合のみ表示する
    if (!isAiKakuninUser(deps.username.value)) {
      layerListCustom = layerListCustom.filter(e => e.name !== 'landAiDetections');
    }
  }

  await deps.geoItemLayers.initializeDataManagers(
    DEFAULT_LAYER_LIST.concat(layerListCustom),
    { user: resolvedUser, masters: deps.masters },
  );
  deps.geoItemLayers.showInitialDataLayers();
  await deps.fetchDevices();

  deps.state.ui.waitSpinner.show = false;
  deps.handleExtParams();

  // 地図はデータロード中 v-show=false (display:none) で初期化されるため、
  // OpenLayers の updateSize が 0 サイズを記録したままになる。
  // 表示可能になった後に再度リサイズし、表示状態で地図サイズを確定させる。
  // サイズ確定後に初期表示範囲へフィットしないと、極小ビューポート基準で
  // 過剰にズームアウトした縮尺になるため、resizeMap で地図サイズを確定させた後に
  // propsの信号をtrueにして子側でフィットさせる。
  nextTick(() => {
    deps.resizeMap();
    deps.state.shouldFitInitialExtent = true;
  });

  void deps.initCar();
  void deps.startGettingRecentComments();
}

interface ShortcutKeyHandlerDeps {
  simpleModal: SimpleModalState;
  imageViewModal: Reactive<ImageViewModalState>;
  closeImageModal: () => void;
  mapElem: ReturnType<typeof provideMapSelection>['mapElem'];
  cancelDeleteMapSelectedElem: () => void;
  movieSearchState: ReturnType<typeof provideMovieSearch>['movieSearch'];
  editMovieTagModal: ReturnType<typeof provideMovieTagEdit>['editMovieTagModal'];
  tryControlMoviePlayer: (srcKey: string) => void;
}

function createShortcutKeyHandler(deps: ShortcutKeyHandlerDeps): (srcKey: string) => void {
  return (srcKey: string) => {
    if (srcKey === 'escape') {
      // モーダル類を閉じる
      if (deps.simpleModal.show) {
        deps.simpleModal.onDismiss();
      } else if (deps.imageViewModal.show) {
        deps.closeImageModal();
      } else if (deps.mapElem.mapDeleteConfirmElem) {
        deps.cancelDeleteMapSelectedElem();
      } else if (deps.movieSearchState.showTagModal) {
        deps.movieSearchState.showTagModal = false;
      } else if (deps.editMovieTagModal.show) {
        deps.editMovieTagModal.show = false;
      } else {
        deps.tryControlMoviePlayer(srcKey);
      }
    } else {
      deps.tryControlMoviePlayer(srcKey);
    }
  };
}

interface BuildTopShellDeps {
  state: Reactive<TopState>;
  tabs: Ref<Tab[]>;
  geoItemsMeta: GeoItemsMeta;
  movieLayerMeta: ShallowRef<MovieLayerMeta>;
  masters: ReturnType<typeof useReactiveMasters>['masters'];
  refExtremeMap: Ref<IExtremeMapRef | undefined>;
  layout: ReturnType<typeof useLayout>;
  hasRoleSuperAdmin: ComputedRef<boolean>;
  isMapReady: ComputedRef<boolean>;
  domainStores: Pick<
    ReturnType<typeof wireDomainStores>,
    'carState' | 'groupScopeMap' | 'updateShowCarIcons' | 'onCarListRowClick' | 'onCarSearchParamsChange'
  >;
}

function buildTopShell(deps: BuildTopShellDeps): TopShellContext {
  return {
    state: deps.state,
    tabs: deps.tabs,
    geoItemsMeta: deps.geoItemsMeta,
    movieLayerMeta: deps.movieLayerMeta,
    masters: deps.masters,
    styles: deps.layout.styles,
    refExtremeMap: deps.refExtremeMap,
    refGeneralSearchBarRow: deps.layout.refGeneralSearchBarRow,
    refPaneLeft: deps.layout.refPaneLeft,
    refPaneCenter: deps.layout.refPaneCenter,
    refMapTopMiscBar: deps.layout.refMapTopMiscBar,
    refMapTopMiscBarLeft: deps.layout.refMapTopMiscBarLeft,
    refMapTopMiscBarRight: deps.layout.refMapTopMiscBarRight,
    refMapSelectedElemInfoArea: deps.layout.refMapSelectedElemInfoArea,
    refPaneRight: deps.layout.refPaneRight,
    dtFormat,
    hasRoleSuperAdmin: deps.hasRoleSuperAdmin,
    isMapReady: deps.isMapReady,
    paneResizeStopped: deps.layout.paneResizeStopped,
    carState: deps.domainStores.carState,
    groupScopeMap: deps.domainStores.groupScopeMap,
    updateShowCarIcons: deps.domainStores.updateShowCarIcons,
    onCarListRowClick: deps.domainStores.onCarListRowClick,
    onCarSearchParamsChange: deps.domainStores.onCarSearchParamsChange,
  };
}

// provideTopView は Top.vue（provider 本体）の setup で呼ぶ。各ドメインストアは
// 子コンポーネント向けに provide しつつ、provider 自身（Top.vue）は inject できないため
// （provide/inject は親→子）、全ストアを返り値で渡す。
export function provideTopView(): TopViewContext {
  const state = reactive<TopState>({
    extremeMapEssentials: null,
    shouldFitInitialExtent: false,
    ui: {
      waitSpinner: {
        show: true,
        msg: WAIT_SPINNER_MSG_DEFAULT,
        msgDefault: WAIT_SPINNER_MSG_DEFAULT,
      },
      simpleModal: {
        title: '確認',
        show: false,
        message: '',
        onClose: () => {},
        onDismiss: () => { state.ui.simpleModal.show = false; },
      },
    },
  });

  // reactive() の UnwrapNestedRefs 型はクラスインスタンスを含む GeoItemsMeta と
  // 互換にならないため、宣言時に一度だけ元の型へ戻す（実体は reactive proxy のまま）。
  const geoItemsMeta = reactive<GeoItemsMeta>(initGeoItemsMeta()) as GeoItemsMeta;
  const movieLayerMeta = shallowRef<MovieLayerMeta>(initMovieLayerMeta());
  const tabs = ref<Tab[]>([]);
  const refExtremeMap = ref<IExtremeMapRef>();

  const auth = useAuth();
  const { masters, resolveMastersPromise } = useReactiveMasters();
  const { isProduction } = useEnv();
  const layout = useLayout(refExtremeMap);

  const { imageViewModal, showImage, closeImageModal } = createImageModal();
  const imageModal = { imageViewModal, showImage, closeImageModal };

  const domainStores = wireDomainStores({ geoItemsMeta, movieLayerMeta, tabs, refExtremeMap, layout, state, masters });
  const {
    moviePlayer, mapSelection, movieTagEdit, movieSearch, geoItemLayers,
    fetchDevices, initCar, startGettingRecentComments, handleExtParams,
  } = domainStores;

  const hasRoleSuperAdmin = computed(() => auth.user?.hasRoleSuperAdmin ?? false);
  const username = computed(() => auth.user?.username ?? '');
  const isMapReady = computed(() => !!state.extremeMapEssentials);

  onMounted(() => {
    void initializeTopView({
      auth, masters, resolveMastersPromise, isProduction, username, state,
      geoItemLayers, fetchDevices, handleExtParams, resizeMap: layout.resizeMap,
      initCar, startGettingRecentComments,
    });
  });

  useShortcutKey(createShortcutKeyHandler({
    simpleModal: state.ui.simpleModal,
    imageViewModal,
    closeImageModal,
    mapElem: mapSelection.mapElem,
    cancelDeleteMapSelectedElem: mapSelection.cancelDeleteMapSelectedElem,
    movieSearchState: movieSearch.movieSearch,
    editMovieTagModal: movieTagEdit.editMovieTagModal,
    tryControlMoviePlayer: moviePlayer.tryControlMoviePlayer,
  }));

  // 画面共有状態（レイアウト・refs・マスタ・車両など、どのドメインにも属さない
  // コーディネーター固有の状態）。
  const shell = buildTopShell({
    state, tabs, geoItemsMeta, movieLayerMeta, masters, refExtremeMap, layout,
    hasRoleSuperAdmin, isMapReady, domainStores,
  });
  provideTopShellCtx(shell);

  // provider 本体（Top.vue）は自分が provide したものを inject できないため、
  // 各ストアを返り値で渡す。子コンポーネントは useXxxStore() で inject する。
  return {
    shell,
    imageModal,
    moviePlayer,
    mapSelection,
    movieSearch,
    geoItemLayers,
    movieTagEdit,
  };
}
